import { useMemo } from "react";
import "../../../css/UserProfileTabs.css";

interface SafetyTrainingTabProps {
    user: any;
    readOnly: boolean;
}

/**
 * SafetyTrainingTab — Admin view of a user's training data.
 * Shows enrolled training plans, completion statistics, and training record table.
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4, 8.5
 */
export const SafetyTrainingTab = ({ user, readOnly }: SafetyTrainingTabProps) => {
    // Parse training module enrollment data from user
    const enrollmentData = useMemo(() => {
        if (!user?.trainingModuleEnrollment) return null;
        try {
            if (typeof user.trainingModuleEnrollment === "string") {
                return JSON.parse(user.trainingModuleEnrollment);
            }
            return user.trainingModuleEnrollment;
        } catch {
            return null;
        }
    }, [user]);

    const enrolledPlans = useMemo(() => {
        if (Array.isArray(enrollmentData)) return enrollmentData;
        if (enrollmentData?.plans && Array.isArray(enrollmentData.plans)) return enrollmentData.plans;
        return [];
    }, [enrollmentData]);

    const formatDate = (dateStr?: string | null): string => {
        if (!dateStr) return "—";
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="upt-training-tab">
            {/* Action Buttons */}
            {!readOnly && (
                <div className="upt-training-actions">
                    <button className="upt-btn-action">Add 8-Hour Refresher</button>
                    <button className="upt-btn-action">New MSHA Training</button>
                </div>
            )}

            {/* Enrolled Plans */}
            <div className="upt-section" style={{ gridColumn: "1 / -1" }}>
                <h3 className="upt-section-title">Enrolled Training Plans</h3>
                {enrolledPlans.length > 0 ? (
                    <div className="upt-training-plans">
                        {enrolledPlans.map((plan: any, idx: number) => (
                            <div key={plan.id || idx} className="upt-training-plan-card">
                                <div className="upt-training-plan-name">
                                    {plan.name || plan.planName || `Plan ${idx + 1}`}
                                </div>
                                <div className="upt-training-plan-meta">
                                    {plan.status && (
                                        <span className={`upt-training-status-badge ${(plan.status || "").toLowerCase()}`}>
                                            {plan.status}
                                        </span>
                                    )}
                                    {plan.enrolledDate && (
                                        <span className="upt-training-plan-date">
                                            Enrolled: {formatDate(plan.enrolledDate)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="upt-empty-state">
                        No training plans enrolled. Use the Safety &amp; Training module to enroll this user.
                    </div>
                )}
            </div>

            {/* Training Progress */}
            <div className="upt-section" style={{ gridColumn: "1 / -1" }}>
                <h3 className="upt-section-title">Training Progress by Category</h3>
                <div className="upt-training-progress-placeholder">
                    <div className="upt-empty-state">
                        Training progress data will be displayed here when the user is enrolled in a plan with categories.
                    </div>
                </div>
            </div>

            {/* Permanent Training Record Table */}
            <div className="upt-section" style={{ gridColumn: "1 / -1" }}>
                <h3 className="upt-section-title">Permanent Training Record</h3>
                <div className="upt-training-table-container">
                    <table className="upt-training-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Type</th>
                                <th>Topic</th>
                                <th>Description</th>
                                <th>Category</th>
                                <th>Evaluated By</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colSpan={7} className="upt-table-empty">
                                    No training records available. Records will appear here as the user completes training topics.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
